import { Injectable, OnDestroy } from '@angular/core'
import { fromEvent, Observable, Subject, takeUntil } from 'rxjs'
import { io, Socket } from 'socket.io-client'
import { AuthService } from 'src/app/services/auth.service'
import { ServerUrlService } from 'src/app/services/server-url.service'
import { AppointmentCancelledEvent, AppointmentCreatedEvent, AppointmentTransferredEvent, BlockedSlotsUpdatedEvent } from './agenda-calendar.models'

@Injectable()
export class AgendaSocketService implements OnDestroy {
  private socket: Socket | null = null
  private destroy$ = new Subject<void>()

  private appointmentTransferred$ = new Subject<AppointmentTransferredEvent>()
  private appointmentCreated$ = new Subject<AppointmentCreatedEvent>()
  private appointmentCancelled$ = new Subject<AppointmentCancelledEvent>()
  private blockedSlotsUpdated$ = new Subject<BlockedSlotsUpdatedEvent[]>()

  constructor(
    private readonly authService: AuthService,
    private readonly serverUrl: ServerUrlService,
  ) {
    this.connect()
  }

  private connect(): void {
    let url = this.serverUrl.url
    if (/:[0-9]{4}/.test(url)) {
      url = url.slice(0, url.length - 4) + '2023'
    }

    this.socket = io(url, {
      transports: ['websocket'],
      path: '/nest-ws',
      auth: {
        token: this.authService.token,
      },
    })

    fromEvent<AppointmentTransferredEvent>(this.socket, 'appointmentTransferred')
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => this.appointmentTransferred$.next(event))

    fromEvent<AppointmentCreatedEvent>(this.socket, 'appointmentCreated')
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => this.appointmentCreated$.next(event))

    fromEvent<AppointmentCancelledEvent>(this.socket, 'appointmentCancelled')
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => this.appointmentCancelled$.next(event))

    fromEvent<BlockedSlotsUpdatedEvent[]>(this.socket, 'blockedSlotsUpdated')
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => this.blockedSlotsUpdated$.next(event))
  }

  joinAgendaRoom(scheduleIds: number[]): void {
    if (!this.socket) return
    for (const id of scheduleIds) {
      this.socket.emit('joinScheduleAppointmentsRoom', id)
      this.socket.emit('joinScheduleBlockedSlotsRoom', id)
    }
  }

  leaveAgendaRoom(scheduleIds: number[]): void {
    if (!this.socket) return
    for (const id of scheduleIds) {
      this.socket.emit('leaveScheduleAppointmentsRoom', id)
      this.socket.emit('leaveScheduleBlockedSlotsRoom', id)
    }
  }

  onAppointmentTransferred(): Observable<AppointmentTransferredEvent> {
    return this.appointmentTransferred$.asObservable()
  }

  onAppointmentCreated(): Observable<AppointmentCreatedEvent> {
    return this.appointmentCreated$.asObservable()
  }

  onAppointmentCancelled(): Observable<AppointmentCancelledEvent> {
    return this.appointmentCancelled$.asObservable()
  }

  onBlockedSlotsUpdated(): Observable<BlockedSlotsUpdatedEvent[]> {
    return this.blockedSlotsUpdated$.asObservable()
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect()
      this.socket = null
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
    this.appointmentTransferred$.complete()
    this.appointmentCreated$.complete()
    this.appointmentCancelled$.complete()
    this.blockedSlotsUpdated$.complete()
    this.disconnect()
  }
}
